import { AppIcon } from '@/components/ui/app-icon';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { usePresence, type PresenceMember } from '@/hooks/use-presence.hook';
import { iconUser } from '@/lib/icons';
import { cn } from '@/lib/utils';
import { useSidebar } from './sidebar-provider.component';

/**
 * Team presence strip, bottom-pinned in the nav rail.
 *
 * An avatar stack of the organization's members with a live online dot, each
 * hoverable for name / email / status. The roster comes over the existing
 * unified WebSocket (see `use-presence.hook.ts`), so "online" means a teammate
 * actually has the app open right now.
 *
 * Hidden for solo organizations — a one-avatar "team" is noise — and collapsed
 * to a single count chip when the rail is collapsed, where a stack would not
 * fit in 62px.
 *
 * The strip is a peripheral signal, not a feature: it sits directly under the
 * nav and above the account card, so the avatars are deliberately small and the
 * whole thing degrades to a bare count once a team is big enough that a row of
 * faces stops being readable at this size.
 */

/** Avatar diameter. Small on purpose — this sits in the chrome, not the page. */
const AVATAR_PX = 18;

/** Avatars shown before the stack truncates to a "+N" chip. */
const MAX_VISIBLE = 3;

/**
 * Past this many members the stack is dropped entirely for a plain count. Even
 * truncated, a row of faces this small stops carrying information for a big
 * team — "8 online" says the useful part.
 */
const STACK_LIMIT = 6;

function statusLabel(member: PresenceMember) {
  if (member.isSelf) return member.online ? 'You · online' : 'You';
  return member.online ? 'Online' : 'Offline';
}

function Dot({ online }: { online: boolean }) {
  return (
    <span
      className={cn(
        'absolute -right-px -bottom-px h-[7px] w-[7px] rounded-full ring-2 ring-[hsl(var(--presence-ring,var(--app-bg)))]',
        online ? 'bg-emerald-500' : 'bg-slate-400 dark:bg-slate-600'
      )}
    />
  );
}

function Avatar({ member }: { member: PresenceMember }) {
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'relative flex shrink-0 items-center justify-center overflow-visible rounded-full',
            // Offline teammates read as present-but-away rather than absent.
            !member.online && 'opacity-60'
          )}
          style={{ height: AVATAR_PX, width: AVATAR_PX }}
        >
          <span
            className='flex items-center justify-center overflow-hidden rounded-full bg-muted ring-2 ring-[hsl(var(--presence-ring,var(--app-bg)))]'
            style={{ height: AVATAR_PX, width: AVATAR_PX }}
          >
            {member.imageUrl ? (
              <img
                src={member.imageUrl}
                alt={member.name}
                className='h-full w-full object-cover'
              />
            ) : (
              <AppIcon
                icon={iconUser}
                className='text-muted-foreground'
                style={{ fontSize: 8 }}
              />
            )}
          </span>
          <Dot online={member.online} />
        </span>
      </TooltipTrigger>
      <TooltipContent side='top' sideOffset={8}>
        <span className='flex flex-col'>
          <span className='font-medium'>{member.name}</span>
          {member.email && (
            <span className='text-tertiary-foreground'>{member.email}</span>
          )}
          <span className='text-tertiary-foreground'>
            {statusLabel(member)}
          </span>
        </span>
      </TooltipContent>
    </Tooltip>
  );
}

/** The roster behind a bare count — who is actually online, names only. */
function RosterTooltip({
  members,
  onlineCount,
}: {
  members: PresenceMember[];
  onlineCount: number;
}) {
  return (
    <span className='flex flex-col gap-0.5'>
      <span className='font-medium'>
        {onlineCount} of {members.length} online
      </span>
      {members
        .filter((m) => m.online)
        .slice(0, 8)
        .map((m) => (
          <span key={m.userId} className='text-tertiary-foreground'>
            {m.name}
            {m.isSelf ? ' (you)' : ''}
          </span>
        ))}
      {onlineCount > 8 && (
        <span className='text-tertiary-foreground'>
          and {onlineCount - 8} more
        </span>
      )}
    </span>
  );
}

/** A dot + number, used by the collapsed rail and by large teams. */
function CountChip({ onlineCount }: { onlineCount: number }) {
  return (
    <span className='flex items-center gap-1.5'>
      <span className='h-[7px] w-[7px] shrink-0 rounded-full bg-emerald-500' />
      <span className='truncate text-xs text-tertiary-foreground'>
        {onlineCount} online
      </span>
    </span>
  );
}

export function SidebarPresence() {
  const { isCollapsed } = useSidebar();
  const { members, onlineCount, isReady } = usePresence();

  if (!isReady) return null;

  const visible = members.slice(0, MAX_VISIBLE);
  const overflow = members.slice(MAX_VISIBLE);

  // Collapsed rail (62px) and large teams both reduce to the same bare count —
  // one has no room for faces, the other no longer benefits from them.
  if (isCollapsed || members.length > STACK_LIMIT) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <div
            className={cn(
              'flex w-full items-center',
              isCollapsed ? 'h-7 justify-center' : 'px-2 py-1'
            )}
          >
            {isCollapsed ? (
              <span className='flex items-center gap-1'>
                <span className='h-[7px] w-[7px] rounded-full bg-emerald-500' />
                <span className='text-xs font-medium text-tertiary-foreground'>
                  {onlineCount}
                </span>
              </span>
            ) : (
              <CountChip onlineCount={onlineCount} />
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side={isCollapsed ? 'right' : 'top'} sideOffset={8}>
          <RosterTooltip members={members} onlineCount={onlineCount} />
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <div className='flex items-center gap-2 px-2 py-1'>
      {/* Negative margin overlaps the avatars into a stack; each keeps an
          app-bg ring so the overlap reads as depth, not collision. */}
      <div className='flex items-center -space-x-1.5'>
        {visible.map((member) => (
          <Avatar key={member.userId} member={member} />
        ))}
        {overflow.length > 0 && (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <span
                className='relative flex shrink-0 items-center justify-center rounded-full bg-muted text-[8px] font-semibold text-muted-foreground ring-2 ring-[hsl(var(--presence-ring,var(--app-bg)))]'
                style={{ height: AVATAR_PX, width: AVATAR_PX }}
              >
                +{overflow.length}
              </span>
            </TooltipTrigger>
            <TooltipContent side='top' sideOffset={8}>
              <span className='flex flex-col gap-0.5'>
                {overflow.map((m) => (
                  <span key={m.userId}>
                    {m.name}
                    <span className='text-tertiary-foreground'>
                      {' '}
                      · {statusLabel(m)}
                    </span>
                  </span>
                ))}
              </span>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      <span className='truncate text-xs text-tertiary-foreground'>
        {onlineCount} online
      </span>
    </div>
  );
}
